// StatisticsPage - Tableau de bord des statistiques globales (connecté au backend)
import { useState, useEffect, useCallback } from "react";
import { BarChart, TrendingUp, Users, Phone, Mail, MessageSquare, Activity, ArrowUpRight, ArrowDownRight, RefreshCw, Loader2, FileText, ClipboardList } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function StatisticsPage() {
  const [period, setPeriod] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/stats/global?period=${period}`);
      setData(res.data);
    } catch (err) {
      console.error("Erreur chargement stats:", err);
      toast.error("Erreur de chargement des statistiques");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const periodLabels = {
    "24h": "24 dernières heures",
    "7d": "7 derniers jours",
    "30d": "30 derniers jours",
    "90d": "90 derniers jours"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const channels = data?.channels || [];
  const agents = data?.agents || [];
  const hasData = data?.has_data;

  return (
    <div className="space-y-6" data-testid="statistics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <BarChart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Statistiques</h1>
            <p className="text-sm text-zinc-400">Vue d'ensemble des performances de la plateforme</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period selector */}
          <div className="flex items-center bg-zinc-900 rounded-lg p-1">
            {["24h", "7d", "30d", "90d"].map((p) => (
              <Button
                key={p}
                size="sm"
                variant={period === p ? "secondary" : "ghost"}
                className="h-8 px-4"
                onClick={() => setPeriod(p)}
              >
                {p}
              </Button>
            ))}
          </div>
          <Button variant="outline" onClick={loadStats}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Message si pas de données */}
      {!hasData && (
        <Card className="bg-zinc-900/50 border-amber-500/30">
          <CardContent className="py-6 text-center">
            <Activity className="w-12 h-12 text-amber-400/50 mx-auto mb-3" />
            <p className="text-zinc-400">Aucune donnée disponible pour cette période.</p>
            <p className="text-sm text-zinc-500 mt-1">Les statistiques s'afficheront dès que l'activité sera enregistrée.</p>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{kpis.interactions_period || 0}</p>
                <p className="text-sm text-zinc-400">Interactions ({period})</p>
                <p className="text-xs text-zinc-500 mt-1">Total: {kpis.interactions_total || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{kpis.calls_audrey || 0}</p>
                <p className="text-sm text-zinc-400">Appels Audrey</p>
                <p className="text-xs text-zinc-500 mt-1">Total: {kpis.calls_total || 0}</p>
              </div>
              <Phone className="w-8 h-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{kpis.emails_traites || 0}</p>
                <p className="text-sm text-zinc-400">Emails traités</p>
                <p className="text-xs text-zinc-500 mt-1">Non lus: {kpis.emails_unread || 0}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{kpis.tickets_crees || 0}</p>
                <p className="text-sm text-zinc-400">Tickets créés</p>
                <p className="text-xs text-zinc-500 mt-1">Ouverts: {kpis.tickets_open || 0}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2 - Stats secondaires */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{kpis.leads_total || 0}</p>
                <p className="text-sm text-zinc-400">Leads totaux</p>
              </div>
              <Users className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{kpis.contents_generated || 0}</p>
                <p className="text-sm text-zinc-400">Contenus générés</p>
              </div>
              <FileText className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{kpis.sms_sent || 0}</p>
                <p className="text-sm text-zinc-400">SMS envoyés</p>
              </div>
              <MessageSquare className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{kpis.cxo_conversations || 0}</p>
                <p className="text-sm text-zinc-400">Messages CxO</p>
              </div>
              <Activity className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Répartition par canal */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg">Répartition par canal</CardTitle>
            <CardDescription>Distribution des interactions sur {periodLabels[period]}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {channels.length === 0 ? (
              <p className="text-zinc-500 text-sm italic text-center py-4">Aucune donnée de canal</p>
            ) : (
              channels.map((ch, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white">{ch.channel}</span>
                    <span className="text-zinc-400">{ch.interactions} ({ch.percent}%)</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${ch.color || 'bg-blue-500'} rounded-full transition-all`}
                      style={{ width: `${ch.percent}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Performance des agents */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg">Performance des agents IA</CardTitle>
            <CardDescription>Métriques de résolution par agent</CardDescription>
          </CardHeader>
          <CardContent>
            {agents.length === 0 ? (
              <p className="text-zinc-500 text-sm italic text-center py-4">Aucune donnée d'agent</p>
            ) : (
              <div className="space-y-4">
                {agents.map((agent, i) => (
                  <div key={i} className="p-4 bg-zinc-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white">{agent.agent}</span>
                      <Badge className="bg-emerald-500/20 text-emerald-400">
                        {agent.resolution_rate}% résolution
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-zinc-400">
                      <span>{agent.period_interactions} sur la période</span>
                      <span className="text-zinc-500">•</span>
                      <span>{agent.interactions} total</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span>Dernière mise à jour : {data?.generated_at ? new Date(data.generated_at).toLocaleString('fr-FR') : '—'}</span>
        <span>Période : {periodLabels[period]}</span>
      </div>
    </div>
  );
}
